// 정책·이벤트 (2026-10-06 신규)
// 기한이 있고 결과에 따라 시나리오가 갈리는 이벤트를 '예정 → 진행 → 종료 → 복기' 로 기록하는 카테고리.
// 데이터: public/data/policy-events.json (내용 수정은 그 파일만 고치고 push)
//
// 서술 원칙 (템플릿 고정):
//  1. 어느 정당·정파가 바람직하다고 쓰지 않는다. 시나리오별 정책 경로만 기술한다.
//  2. 확률·의석 전망은 예측기관 추정치를 출처와 함께 인용하고 여론조사 오차를 명시한다.
//  3. 과거 사례의 시장 반응은 반복이 보장되지 않음을 명시한다.
//  4. 투자 자문이 아니라 판단 재료임을 표기한다.
import { useState, useEffect, useMemo, useRef } from "react";
import { C, mono, BASE_URL } from "./utils";
import { Ico } from "./lib/icons";

let _cache = null;
export function usePolicyData() {
  const [data, setData] = useState(_cache);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (_cache) return;
    let alive = true;
    fetch(`${process.env.PUBLIC_URL || ""}/data/policy-events.json`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error("load"))))
      .then(j => { _cache = j; if (alive) setData(j); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);
  return { data, failed };
}

const kstToday = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
const dday = (date) => Math.round((new Date(date + "T00:00:00Z") - new Date(kstToday() + "T00:00:00Z")) / 86400000);
const ddayLabel = (n) => (n === 0 ? "D-DAY" : n > 0 ? `D-${n}` : `D+${-n}`);

const STATUS = { "예정": "#8b5cf6", "진행": "#10b981", "종료": "#94a3b8" };
const TYPE_COLOR = { "관세·통상": "#f97316", "금리·연준": "#06b6d4", "재정·예산": "#6366f1", "산업규제": "#eab308", "산업·기술": "#a855f7", "지정학": "#ef4444", "대미투자": "#10b981" };
const REV = { fixed: "#94a3b8", none: "#94a3b8", congress: "#f59e0b", election: "#ef4444" };
const DIR = { "수혜": C.up, "피해": C.down, "쟁점": "#f59e0b", "중립": "#94a3b8" };

const card = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 14, padding: 20, marginBottom: 14 };
const label = { fontSize: 11, color: "var(--text-muted)", marginBottom: 4 };
const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-sub)", padding: "9px 12px", borderBottom: "1px solid var(--grid-line)", background: "var(--cal-head-bg)", whiteSpace: "nowrap" };
const td = { fontSize: 13, color: "var(--text)", padding: "10px 12px", borderBottom: "1px solid var(--grid-line)", verticalAlign: "top", lineHeight: 1.6 };

const Chip = ({ color, children, solid }) => (
  <span style={{ display: "inline-block", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, color: solid ? "#fff" : color, background: solid ? color : color + "22", whiteSpace: "nowrap" }}>{children}</span>
);
const Title = ({ icon, children, right }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", display: "flex", alignItems: "center" }}>{icon && <Ico n={icon} />}{children}</div>
    {right}
  </div>
);
const Src = ({ url }) => url ? (
  <a href={url} target="_blank" rel="noopener noreferrer" title="출처 열기" style={{ color: "var(--link)", marginLeft: 6 }}><Ico n="external-link" size="0.9em" mr="0" tint={false} /></a>
) : null;
const Strength = ({ n, color }) => (
  <span title={`강도 ${n}/3`} style={{ letterSpacing: 1, color, fontSize: 9, verticalAlign: "middle" }}>{"●".repeat(n)}<span style={{ opacity: 0.25 }}>{"●".repeat(3 - n)}</span></span>
);
const period = (e) => e.end ? `${e.start} ~ ${e.end}` : e.start;

// ── 1. 이벤트 캘린더 ──────────────────────────────────────────
function EventDetail({ e }) {
  const rows = [["유형", e.type], ["국가", e.country], ["결정 주체", e.decider], ["근거", e.basis], ["가역성", `${e.reversibility.label} — ${e.reversibility.note}`], ["발생일 · 만료일", period(e)], ["상태", e.status]];
  return (
    <div style={{ padding: "4px 0 6px" }}>
      <div style={{ fontSize: 13.5, color: "var(--text)", lineHeight: 1.75, marginBottom: 14, wordBreak: "keep-all" }}>{e.summary}</div>
      <div style={{ border: "1px solid var(--grid-line)", borderRadius: 10, overflow: "hidden", marginBottom: 14 }}>
        {rows.map(([k, v], i) => (
          <div key={k} style={{ display: "flex", borderTop: i ? "1px solid var(--grid-line)" : "none" }}>
            <div style={{ width: 120, flexShrink: 0, padding: "8px 12px", fontSize: 12, color: "var(--text-sub)", background: "var(--cal-head-bg)", fontWeight: k === "가역성" ? 700 : 500 }}>{k}</div>
            <div style={{ padding: "8px 12px", fontSize: 13, color: "var(--text)", lineHeight: 1.6 }}>{v}</div>
          </div>
        ))}
      </div>
      {e.result && <div style={{ fontSize: 13, color: "var(--text)", marginBottom: 12 }}><Chip color="#94a3b8">결과</Chip> <span style={{ marginLeft: 6 }}>{e.result}</span></div>}
      {e.checkpoints?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={label}>체크포인트</div>
          {e.checkpoints.map((c, i) => (
            <div key={i} style={{ display: "flex", gap: 10, fontSize: 13, padding: "5px 0", color: "var(--text)" }}>
              <span style={{ ...mono, color: "var(--text-sub)", width: 92, flexShrink: 0 }}>{c.approx || c.date}</span>
              <span style={{ flex: 1 }}>{c.label}{c.result && <span style={{ color: "var(--text-sub)" }}> → {c.result}</span>}</span>
            </div>
          ))}
        </div>
      )}
      {e.sources?.length > 0 && (
        <div>
          <div style={label}>출처</div>
          {e.sources.map((s, i) => (
            <div key={i} style={{ fontSize: 12.5, padding: "2px 0" }}><a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--link)", textDecoration: "none" }}>{s.title}</a></div>
          ))}
        </div>
      )}
    </div>
  );
}

function CalendarTab({ data, onOpenScenario, focusId }) {
  const [status, setStatus] = useState("전체");
  const [type, setType] = useState("전체");
  const [open, setOpen] = useState(focusId || null);
  useEffect(() => { if (focusId) { setOpen(focusId); setStatus("전체"); setType("전체"); } }, [focusId]);
  const today = kstToday();
  const upcoming = useMemo(() => data.events.flatMap(e => (e.checkpoints || []).filter(c => !c.result && c.date >= today && !/미정/.test(c.approx || "")).map(c => ({ ...c, event: e })))
    .sort((a, b) => a.date.localeCompare(b.date)).slice(0, 6), [data, today]);
  const types = ["전체", ...Array.from(new Set(data.events.map(e => e.type)))];
  const list = data.events.filter(e => (status === "전체" || e.status === status) && (type === "전체" || e.type === type))
    .sort((a, b) => b.start.localeCompare(a.start));
  const fbtn = (on) => ({ padding: "5px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: on ? 700 : 500, border: "1px solid " + (on ? "var(--primary)" : "var(--border)"), background: on ? "var(--sb-active-bg)" : "transparent", color: on ? "var(--sb-active-text)" : "var(--text-sub)" });

  return (
    <>
      {upcoming.length > 0 && (
        <div style={card}>
          <Title icon="bell">다가오는 체크포인트</Title>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 10 }}>
            {upcoming.map((c, i) => {
              const n = dday(c.date);
              return (
                <div key={i} style={{ border: "1px solid var(--grid-line)", borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ ...mono, fontSize: 12, color: "var(--text-sub)" }}>{c.approx || c.date}</span>
                    <span style={{ ...mono, fontSize: 12, fontWeight: 700, color: n <= 7 ? C.up : "var(--gold)" }}>{c.approx ? "약 " : ""}{ddayLabel(n)}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.55, wordBreak: "keep-all" }}>{c.label}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>{c.event.title}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={card}>
        <Title icon="calendar-days">이벤트 목록</Title>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {["전체", "예정", "진행", "종료"].map(s => <button key={s} onClick={() => setStatus(s)} style={fbtn(status === s)}>{s}</button>)}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {types.map(s => <button key={s} onClick={() => setType(s)} style={fbtn(type === s)}>{s}</button>)}
        </div>
        <div style={{ border: "1px solid var(--grid-line)", borderRadius: 10, overflow: "hidden" }}>
          {list.length === 0 && <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "var(--text-muted)" }}>조건에 맞는 이벤트가 없습니다.</div>}
          {list.map((e, i) => (
            <div key={e.id} style={{ borderTop: i ? "1px solid var(--grid-line)" : "none" }}>
              <div onClick={() => setOpen(open === e.id ? null : e.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", cursor: "pointer", background: open === e.id ? "var(--cal-hover)" : "transparent", flexWrap: "wrap" }}>
                <span style={{ ...mono, fontSize: 12, color: "var(--text-sub)", width: 86, flexShrink: 0 }}>{e.start}</span>
                <span style={{ flex: 1, minWidth: 180, fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{e.title}</span>
                <Chip color={TYPE_COLOR[e.type] || "#94a3b8"}>{e.type}</Chip>
                <Chip color={REV[e.reversibility.level]}>{e.reversibility.label}</Chip>
                <Chip color={STATUS[e.status]} solid>{e.status}</Chip>
              </div>
              {open === e.id && (
                <div style={{ padding: "4px 16px 14px" }}>
                  <EventDetail e={e} />
                  {e.scenarios?.length > 0 && (
                    <button onClick={() => onOpenScenario(e.id)} style={{ marginTop: 12, padding: "7px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--link)", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                      시나리오 맵 보기 <Ico n="arrow-right" size="0.95em" mr="0" tint={false} />
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ── 2. 시나리오 맵 ────────────────────────────────────────────
const pill = (on) => ({ padding: "5px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer", fontWeight: on ? 700 : 500, border: "1px solid " + (on ? "var(--primary)" : "var(--border)"), background: on ? "var(--sb-active-bg)" : "transparent", color: on ? "var(--sb-active-text)" : "var(--text-sub)" });

const StockRows = ({ title, color, list, onOpenStock, us }) => (
  <div style={{ padding: "12px 16px 6px", border: "1px solid var(--grid-line)", borderRadius: 10 }}>
    <div style={{ fontSize: 14, fontWeight: 800, color, marginBottom: 6 }}>{title}</div>
    {list.map((x, i) => (
      <div key={i} style={{ padding: "9px 0", borderTop: i ? "1px solid var(--border-light)" : "none" }}>
        <div onClick={() => !us && onOpenStock && x.code && onOpenStock(x.code)} title={us ? x.code : "주식 검색에서 열기"} style={{ display: "inline-flex", alignItems: "baseline", gap: 6, fontSize: 13.5, fontWeight: 700, color: "var(--text)", cursor: !us && onOpenStock ? "pointer" : "default", marginBottom: 4 }}>
          {x.name}<span style={{ ...mono, fontSize: 10.5, color: "var(--text-muted)", fontWeight: 400 }}>{x.code}</span>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-sub)", lineHeight: 1.7, wordBreak: "keep-all" }}>{x.why}</div>
      </div>
    ))}
  </div>
);

// 시나리오 하나를 골라 설명문 → 핵심 인물 → 종목(국내·미국) 순서로 읽는 화면
const Sub = ({ children, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15.5, fontWeight: 800, color: color || "var(--text)", margin: "24px 0 10px", letterSpacing: "-0.01em" }}>
    <span style={{ width: 4, height: 16, borderRadius: 2, background: color || "var(--primary)", flexShrink: 0 }} />{children}
  </div>
);
const Tags = ({ items, color }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
    {items.map((t, i) => <span key={i} style={{ fontSize: 12.5, padding: "4px 9px", borderRadius: 7, color: "var(--text)", background: (color || "#94a3b8") + "1c", border: "1px solid " + (color || "#94a3b8") + "33", lineHeight: 1.5 }}>{t}</span>)}
  </div>
);

// 인물 사진: 세로로 긴 4:5 직사각형. 공식 사진이 없으면 이름(성)으로, 기관이면 아이콘
const PW = 52, PH = 65;
function Portraits({ x }) {
  const box = { width: PW, height: PH, borderRadius: 8, flexShrink: 0, border: "1px solid var(--grid-line)", background: "var(--cal-head-bg)", overflow: "hidden" };
  if (x.photos?.length) return (
    <div style={{ display: "flex", gap: 4 }}>
      {x.photos.map(id => <img key={id} src={`${process.env.PUBLIC_URL || ""}/img/people/${id}.jpg`} alt="" loading="lazy" width={PW} height={PH} style={{ ...box, objectFit: "cover", display: "block" }} />)}
    </div>
  );
  const last = String(x.name).split(" ").pop();
  return (
    <div style={{ ...box, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-sub)", fontSize: x.org ? 22 : 13, fontWeight: 700, textAlign: "center", lineHeight: 1.2, padding: 4 }}>
      {x.org ? <Ico n="landmark" mr="0" tint={false} /> : last}
    </div>
  );
}

function ScenarioDetail({ e, onOpenStock }) {
  const [sid, setSid] = useState(e.scenarios[0].id);
  const s = e.scenarios.find(x => x.id === sid) || e.scenarios[0];
  const two = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 10 };
  return (
    <div style={card}>
      <Title icon="git-fork">시나리오</Title>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8, marginBottom: 6 }}>
        {e.scenarios.map(x => {
          const on = x.id === s.id;
          return (
            <button key={x.id} onClick={() => setSid(x.id)} style={{ textAlign: "left", padding: "11px 12px", borderRadius: 10, cursor: "pointer", border: "1px solid " + (on ? "var(--primary)" : "var(--grid-line)"), background: on ? "var(--sb-active-bg)" : "transparent" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                <span style={{ width: 22, height: 22, borderRadius: 7, background: "var(--gold-bg)", color: "var(--gold)", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, ...mono }}>{x.id}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)", lineHeight: 1.35 }}>{x.short || x.name}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>{x.tail ? "꼬리 위험" : (x.liveProb ? `오늘 ${x.liveProb}` : x.refProb)}</div>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
        <span style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>{s.name}</span>
        {s.tail && <Chip color="#ef4444">꼬리 위험</Chip>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 18px", marginTop: 6, fontSize: 12, color: "var(--text-muted)" }}>
        {s.when && <span>시점 <b style={{ color: "var(--text)", fontWeight: 600 }}>{s.when}</b></span>}
        {s.refProb && <span>예측 <b style={{ color: "var(--text)", fontWeight: 600 }}>{s.refProb}</b></span>}
        {s.liveProb && <span>오늘 <b style={{ color: "var(--primary)", fontWeight: 600 }}>{s.liveProb}</b></span>}
        <span>본인 추정 <b style={{ ...mono, color: "var(--text)" }}>{s.prob == null ? "미입력" : s.prob + "%"}</b></span>
      </div>

      <Sub>설명</Sub>
      {(s.narrative || [s.premise]).map((t, i) => <p key={i} style={{ fontSize: 13.5, color: "var(--text)", lineHeight: 1.85, margin: "0 0 10px", wordBreak: "keep-all" }}>{t}</p>)}

      {s.people?.length > 0 && <>
        <Sub>이 시나리오를 움직이는 사람</Sub>
        <div style={{ border: "1px solid var(--grid-line)", borderRadius: 10, overflow: "hidden" }}>
          {s.people.map((x, i) => (
            <div key={i} style={{ display: "flex", gap: 14, padding: "12px 14px", borderTop: i ? "1px solid var(--grid-line)" : "none", flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ display: "flex", gap: 12, width: 290, flexShrink: 0, alignItems: "flex-start" }}>
                <Portraits x={x} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", lineHeight: 1.4 }}>{x.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 3, lineHeight: 1.5 }}>{x.role}</div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 240, fontSize: 13, color: "var(--text)", lineHeight: 1.65, wordBreak: "keep-all" }}>{x.why}<Src url={x.url} /></div>
            </div>
          ))}
        </div>
        {e.photoCredit && <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 6 }}>{e.photoCredit}</div>}
      </>}

      {s.facts?.length > 0 && <>
        <Sub>현재 확인된 사실</Sub>
        {s.facts.map((f, i) => <div key={i} style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.7, padding: "3px 0", wordBreak: "keep-all" }}>· {f.text}<Src url={f.url} /></div>)}
      </>}

      <Sub>{s.tail ? "예상 경로" : "가능해지는 것"}</Sub>
      <Tags items={s.enables} />
      <div style={{ ...two, marginTop: 4 }}>
        <div><Sub color={C.up}>수혜 업종</Sub><Tags items={s.benefit} color={C.up} /></div>
        <div><Sub color={C.down}>피해 업종</Sub><Tags items={s.harm} color={C.down} /></div>
      </div>

      <Sub>국내 주식</Sub>
      <div style={two}>
        {s.shake?.length > 0 && <StockRows title="크게 흔들릴 종목" color={C.down} list={s.shake} onOpenStock={onOpenStock} />}
        {s.rebound?.length > 0 && <StockRows title={s.tail ? "방어 · 진정 시 반등할 종목" : "크게 반등할 종목"} color={C.up} list={s.rebound} onOpenStock={onOpenStock} />}
      </div>
      {(s.usShake?.length > 0 || s.usRebound?.length > 0) && <>
        <Sub>미국 주식</Sub>
        <div style={two}>
          {s.usShake?.length > 0 && <StockRows us title="크게 흔들릴 종목" color={C.down} list={s.usShake} />}
          {s.usRebound?.length > 0 && <StockRows us title={s.tail ? "방어 · 진정 시 반등할 종목" : "크게 반등할 종목"} color={C.up} list={s.usRebound} />}
        </div>
      </>}

      {s.limit && <><Sub color="#f59e0b">상충 요인 · 한계</Sub><div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.75, wordBreak: "keep-all" }}>{s.limit}</div></>}
    </div>
  );
}

// ── 상원 판세 지도 (2026-10-08) ──
// 35개 주의 Cook 등급을 색으로. 미국 정당 관례대로 민주 파랑·공화 빨강 (주가 등락 색과는 별개)
const RATING = {
  sd: { label: "민주 확실", color: "#1d4ed8" }, ld: { label: "민주 유력", color: "#3b82f6" }, lnd: { label: "민주 우세", color: "#93c5fd" },
  t: { label: "초접전", color: "#f59e0b" },
  lnr: { label: "공화 우세", color: "#fca5a5" }, lr: { label: "공화 유력", color: "#ef4444" }, sr: { label: "공화 확실", color: "#b91c1c" },
};
let _usMap = null;
const LABEL_NUDGE = { MI: [22, 38], ME: [-4, 6] }; // 무게중심이 호수·바다 쪽에 걸리는 주
function SenateMap({ e }) {
  const m = e.races?.map;
  const [geo, setGeo] = useState(_usMap);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    if (_usMap) return;
    let alive = true;
    fetch(`${process.env.PUBLIC_URL || ""}/data/us-states.json`).then(r => r.ok ? r.json() : null).then(j => { if (j) { _usMap = j; if (alive) setGeo(j); } }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!m) return null;
  const avgOf = (name) => (e.races.senate || []).find(x => x.state === name);
  const cnt = (keys) => Object.values(m.states).filter(v => keys.includes(v.r)).length;
  const dSafe = m.notUp.D + cnt(["sd", "ld", "lnd"]), rSafe = m.notUp.R + cnt(["sr", "lr", "lnr"]), toss = cnt(["t"]);
  const tossLead = Object.values(m.states).filter(v => v.r === "t").map(v => avgOf(v.name)).filter(Boolean);
  const leadD = tossLead.filter(x => String(x.avg).startsWith(String(x.dem).split(" ").pop())).length; // 평균 앞자리가 민주 후보 성이면 민주 우위
  const need = 51 - dSafe;
  const h = hover && m.states[hover];
  const hAvg = h && avgOf(h.name);
  return (
    <div style={card}>
      <Title icon="map-pin" right={<span style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>{m.source} · {m.asOf}</span>}>상원 판세 지도</Title>
      {/* 의석 막대: 비개선 + 확실·유력·우세 + 초접전 */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
        <span style={{ color: "#3b82f6" }}>민주 {dSafe}석</span>
        <span style={{ color: "#f59e0b" }}>초접전 {toss}곳</span>
        <span style={{ color: "#ef4444" }}>공화 {rSafe}석</span>
      </div>
      <div style={{ position: "relative", display: "flex", height: 14, borderRadius: 7, overflow: "hidden", marginBottom: 6 }}>
        <div style={{ width: `${dSafe}%`, background: "#3b82f6" }} />
        <div style={{ width: `${toss}%`, background: "#f59e0b" }} />
        <div style={{ width: `${rSafe}%`, background: "#ef4444" }} />
        <div style={{ position: "absolute", left: "50.5%", top: -2, bottom: -2, width: 2, background: "var(--text)" }} title="과반 51석" />
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-sub)", lineHeight: 1.7, marginBottom: 12, wordBreak: "keep-all" }}>
        이번에 선거가 없는 의석과 확실·유력·우세 지역을 더하면 민주 {dSafe}석, 공화 {rSafe}석입니다. 민주당이 다수당(51석)이 되려면 초접전 {toss}곳 중 <b style={{ color: "var(--text)" }}>{need}곳 이상</b>을 이겨야 합니다(50대 50이면 부통령이 공화당이라 공화 다수).
        {tossLead.length > 0 && <> 지금 여론조사 평균으로는 초접전 {tossLead.length}곳 중 민주 후보가 {leadD}곳, 공화 후보가 {tossLead.length - leadD}곳에서 앞서 있습니다(대부분 오차 범위 안).</>}
      </div>
      <div style={{ position: "relative" }}>
        {geo ? (
          <svg viewBox={`0 0 ${geo.w} ${geo.h}`} style={{ width: "100%", maxWidth: 780, height: "auto", display: "block", margin: "0 auto" }} role="img" aria-label="2026 상원 선거 주별 판세 지도">
            {Object.entries(geo.states).map(([ab, g]) => {
              const v = m.states[ab];
              return (
                <path key={ab} d={g.d} fill={v ? RATING[v.r].color : "var(--border)"} stroke="var(--bg-card)" strokeWidth={1}
                  style={{ cursor: v ? "pointer" : "default", opacity: hover && hover !== ab ? 0.75 : 1 }}
                  onMouseEnter={() => v && setHover(ab)} onMouseLeave={() => setHover(null)} onClick={() => v && setHover(ab)} />
              );
            })}
            {Object.entries(geo.states).map(([ab, g]) => {
              const v = m.states[ab];
              if (!v || v.r !== "t") return null;
              const a = avgOf(v.name);
              const [ox, oy] = LABEL_NUDGE[ab] || [0, 0];
              return (
                <g key={ab + "l"} style={{ pointerEvents: "none" }} transform={`translate(${ox} ${oy})`}>
                  <text x={g.c[0]} y={g.c[1] - 2} textAnchor="middle" fontSize="13" fontWeight="700" fill="#1f2937">{ab}</text>
                  {a && <text x={g.c[0]} y={g.c[1] + 13} textAnchor="middle" fontSize="11" fontWeight="600" fill="#1f2937">{a.avg}</text>}
                </g>
              );
            })}
          </svg>
        ) : <div style={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 13 }}>지도 불러오는 중…</div>}
        {h && (
          <div style={{ position: "absolute", left: 12, bottom: 12, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: "var(--text)", boxShadow: "0 4px 14px rgba(0,0,0,0.18)", minWidth: 200 }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{h.name} <span style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>{hover}</span></div>
            <div><span style={{ color: RATING[h.r].color, fontWeight: 700 }}>{RATING[h.r].label}</span> · 현재 {h.held === "D" ? "민주" : "공화"} 의석 ({h.inc})</div>
            {hAvg && <div style={{ marginTop: 4, color: "var(--text-sub)" }}>{hAvg.dem}(민) vs {hAvg.rep}(공) · 평균 <b style={{ color: "var(--text)" }}>{hAvg.avg}</b></div>}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginTop: 10, fontSize: 11.5, color: "var(--text-sub)" }}>
        {["sd", "ld", "lnd", "t", "lr", "sr"].map(k => <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: RATING[k].color }} />{RATING[k].label} {cnt([k])}</span>)}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: "var(--border)" }} />이번 선거 없음</span>
      </div>
      <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 8, lineHeight: 1.6 }}>{m.note}<Src url={m.url} /></div>
    </div>
  );
}

// 승부를 가르는 선거구 — 어느 후보가 이기면 어느 시나리오로 가는지
function Races({ e }) {
  const r = e.races;
  if (!r) return null;
  const rc = (t) => t.includes("초접전") ? "#f59e0b" : t.includes("민주") ? "#3b82f6" : "#ef4444";
  return (
    <div style={card}>
      <Title icon="map-pin" right={<span style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>기준 {r.asOf}</span>}>승부를 가르는 선거구</Title>
      <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.75, marginBottom: 12, wordBreak: "keep-all" }}>{r.note}<Src url={r.url} /></div>
      <div style={{ overflowX: "auto", border: "1px solid var(--grid-line)", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
          <thead><tr><th style={th}>상원 선거구</th><th style={th}>의석 상태</th><th style={th}>민주당 후보</th><th style={th}>공화당 후보</th><th style={th}>판세</th><th style={th}>여론조사 평균</th></tr></thead>
          <tbody>
            {r.senate.map((x, i) => (
              <tr key={i}>
                <td style={{ ...td, fontWeight: 700, whiteSpace: "nowrap" }}>{x.state}</td>
                <td style={{ ...td, fontSize: 12, color: "var(--text-sub)" }}>{x.seat}</td>
                <td style={td}>{x.dem}</td><td style={td}>{x.rep}</td>
                <td style={{ ...td, whiteSpace: "nowrap" }}><Chip color={rc(x.rating)}>{x.rating}</Chip></td>
                <td style={{ ...td, ...mono, fontSize: 12, whiteSpace: "nowrap", color: x.live ? "var(--primary)" : "var(--text)" }}>{x.avg}{x.live && <Src url={x.url} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-sub)", lineHeight: 1.75, marginTop: 12, wordBreak: "keep-all" }}>{r.house}<Src url={r.pollUrl} /></div>
    </div>
  );
}

// 종목 × 시나리오: 한 종목이 시나리오마다 어느 쪽에 서는지 한눈에
function StockScenarioTable({ e, onOpenStock }) {
  const [mkt, setMkt] = useState("kr");
  const upKey = mkt === "kr" ? "rebound" : "usRebound", dnKey = mkt === "kr" ? "shake" : "usShake";
  const names = [];
  e.scenarios.forEach(s => [...(s[upKey] || []), ...(s[dnKey] || [])].forEach(x => { if (!names.find(n => n.name === x.name)) names.push({ name: x.name, code: x.code }); }));
  if (names.length === 0 && mkt === "kr") return null;
  const cell = (s, nm) => {
    const up = (s[upKey] || []).find(x => x.name === nm), dn = (s[dnKey] || []).find(x => x.name === nm);
    if (up && dn) return { t: "▼ → ▲", c: "#f59e0b", why: `${dn.why} → ${up.why}` };
    if (up) return { t: "▲ 반등", c: C.up, why: up.why };
    if (dn) return { t: "▼ 흔들림", c: C.down, why: dn.why };
    return null;
  };
  return (
    <div style={card}>
      <Title icon="grid-3x3" right={<div style={{ fontSize: 11, color: "var(--text-sub)" }}><span style={{ color: C.up }}>▲</span> 반등 · <span style={{ color: C.down }}>▼</span> 흔들림 · <span style={{ color: "#f59e0b" }}>▼ → ▲</span> 흔들린 뒤 되돌림</div>}>시나리오별 종목표</Title>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        {[["kr", "국내 주식"], ["us", "미국 주식"]].map(([k, t]) => <button key={k} onClick={() => setMkt(k)} style={pill(mkt === k)}>{t}</button>)}
      </div>
      {e.stockNote && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10, lineHeight: 1.6 }}>{e.stockNote}</div>}
      <div style={{ overflowX: "auto", border: "1px solid var(--grid-line)", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
          <thead><tr><th style={th}>종목</th>{e.scenarios.map(s => <th key={s.id} style={{ ...th, textAlign: "center", whiteSpace: "normal", minWidth: 96 }}><span style={{ ...mono, color: "var(--gold)" }}>{s.id}</span> {s.short || s.name.replace(/ \(.*\)/, "")}</th>)}</tr></thead>
          <tbody>
            {names.map(n => (
              <tr key={n.name}>
                <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap", cursor: mkt === "kr" && onOpenStock ? "pointer" : "default" }} onClick={() => mkt === "kr" && onOpenStock && n.code && onOpenStock(n.code)}>{n.name}{mkt === "us" && <span style={{ ...mono, fontSize: 11, color: "var(--text-muted)", marginLeft: 6, fontWeight: 400 }}>{n.code}</span>}</td>
                {e.scenarios.map(s => { const c = cell(s, n.name); return (
                  <td key={s.id} title={c ? c.why : ""} style={{ ...td, textAlign: "center", borderLeft: "1px solid var(--grid-line)", fontSize: 12, fontWeight: 700, color: c ? c.c : "var(--text-muted)", whiteSpace: "nowrap" }}>{c ? c.t : "·"}</td>
                ); })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── 매일 자동 업데이트 (서버 /election-live, 매일 06:40 KST) ──
// 숫자(여론조사·확률·경합지 평균)와 최근 뉴스만 바뀜. 설명문·인물·종목은 policy-events.json 그대로
// 같은 화면에서 여러 곳이 써도 서버 요청은 한 번만 (상단 '업데이트' 표시 + 중간선거 시나리오)
let _liveReq = null;
function useElectionLive(on) {
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!on) return;
    let alive = true;
    if (!_liveReq) _liveReq = fetch(`${BASE_URL}/election-live`).then(r => (r.ok ? r.json() : null)).catch(() => null).then(j => { if (!j) _liveReq = null; return j; });
    _liveReq.then(j => { if (alive && j && j.date) setLive(j); });
    return () => { alive = false; };
  }, [on]);
  return live;
}
// 서버 저장 시각(UTC) → "10-14 06:40" (한국 시간)
const kstStamp = (iso) => { const t = Date.parse(iso || ""); if (isNaN(t)) return ""; const k = new Date(t + 9 * 3600 * 1000).toISOString(); return `${k.slice(5, 10)} ${k.slice(11, 16)}`; };

const fmtP = (o) => o && o.value != null ? `${o.source || ""} ${o.value}%`.trim() : null;
function applyLive(e, L) {
  if (!L) return e;
  const ind = (e.indicators || []).map(r => {
    const set = (value, note, src) => src ? { ...r, value, note: `${src.source || ""} · 자동 업데이트`.trim(), asOf: src.asOf || L.date, url: src.url, live: true } : r;
    if (r.label === "일반투표 선호도" && L.genericBallot) { const g = L.genericBallot, m = +(g.dem - g.rep).toFixed(1); return set(`민주 ${g.dem}% / 공화 ${g.rep}% (D${m >= 0 ? "+" : ""}${m})`, null, g); }
    if (r.label === "트럼프 지지율" && L.trumpApproval) return set(`지지 ${L.trumpApproval.approve}% / 불지지 ${L.trumpApproval.disapprove}%`, null, L.trumpApproval);
    if (r.label === "하원 민주 다수 확률" && L.houseDemProb) return set(fmtP(L.houseDemProb), null, L.houseDemProb);
    if (r.label === "상원 전망" && L.senateDemProb) return set(`민주 다수 확률 ${fmtP(L.senateDemProb)}`, null, L.senateDemProb);
    if (r.label === "민주 양원 장악 확률" && L.bothDemProb) return set(fmtP(L.bothDemProb), null, L.bothDemProb);
    return r;
  });
  const races = e.races && L.senateRaces?.length ? { ...e.races, asOf: L.date, senate: e.races.senate.map(x => { const v = L.senateRaces.find(y => y.state === x.state); return v ? { ...x, avg: v.avg, live: true, url: v.url } : x; }) } : e.races;
  const lp = { A: L.bothRepProb, B: L.splitProb, C: L.bothDemProb };
  const scenarios = e.scenarios.map(s => lp[s.id] ? { ...s, liveProb: `${fmtP(lp[s.id])} (${lp[s.id].asOf || L.date})` } : s);
  return { ...e, indicators: ind, races, scenarios };
}

function LiveCard({ L }) {
  if (!L) return null;
  const t = L.timestamp ? new Date(new Date(L.timestamp).getTime() + 9 * 3600 * 1000).toISOString().slice(11, 16) : "";
  return (
    <div style={{ ...card, borderColor: "var(--primary)" }}>
      <Title icon="bell" right={<span style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>{L.date} {t} 자동 업데이트</span>}>오늘의 변화</Title>
      {L.flags?.length > 0 && L.flags.map((f, i) => (
        <div key={i} style={{ fontSize: 13, fontWeight: 600, color: "#ef4444", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "8px 12px", marginBottom: 8, lineHeight: 1.6 }}>무효화 조건 점검 · {f.text}</div>
      ))}
      {L.changes?.length > 0
        ? L.changes.map((c, i) => <div key={i} style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.7, padding: "2px 0" }}>· {c}</div>)
        : <div style={{ fontSize: 13, color: "var(--text-sub)", lineHeight: 1.7 }}>어제와 비교해 1%p 이상 바뀐 숫자는 없습니다.</div>}
      {L.news?.length > 0 && <>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-sub)", margin: "14px 0 6px" }}>최근 48시간 관련 뉴스</div>
        {L.news.map((n, i) => (
          <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid var(--border-light)" : "none" }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{n.titleKo}<Src url={n.url} /><span style={{ ...mono, fontSize: 11, color: "var(--text-muted)", fontWeight: 400, marginLeft: 8 }}>{n.date}</span></div>
            <div style={{ fontSize: 12.5, color: "var(--text-sub)", lineHeight: 1.65, marginTop: 3, wordBreak: "keep-all" }}>{n.summaryKo}</div>
          </div>
        ))}
      </>}
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 10, lineHeight: 1.6 }}>숫자는 웹 검색 결과에서 출처가 확인된 것만 반영합니다. 시나리오 설명·인물·종목은 사람이 검토해 고칩니다.</div>
    </div>
  );
}

// 시나리오가 있는 이벤트 순서 — 상단 바로가기와 같게: 다가오는 이벤트(날짜순) → 진행 중 → 끝난 이벤트
const scenarioEvents = (data) => {
  const today = kstToday();
  const rank = (x) => x.start >= today ? 0 : x.status === "종료" ? 2 : 1; // 중간선거처럼 '진행' 상태라도 날짜가 남았으면 다가오는 이벤트
  return data.events.filter(e => e.scenarios?.length).sort((a, b) => rank(a) - rank(b) || a.start.localeCompare(b.start));
};
// 처음 열 때 보여줄 이벤트: 대표 이벤트(지금은 중간선거), 없으면 가장 가까운 이벤트
const defaultEvent = (list) => list.find(x => x.featured) || list[0];

function ScenarioTab({ data, eventId, setEventId, onOpenStock }) {
  const withSc = scenarioEvents(data);
  const e0 = withSc.find(x => x.id === eventId) || defaultEvent(withSc);
  const live = useElectionLive(e0?.id === "us-midterm-2026");
  if (!e0) return <div style={card}>시나리오가 등록된 이벤트가 없습니다.</div>;
  const e = e0.id === "us-midterm-2026" ? applyLive(e0, live) : e0;
  const n = dday(e.start);
  return (
    <>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>{e.title}</div>
          <Chip color={STATUS[e.status]} solid>{e.status}</Chip>
          <Chip color={REV[e.reversibility.level]}>{e.reversibility.label}</Chip>
          <span style={{ ...mono, fontSize: 13, color: "var(--text-sub)", marginLeft: "auto" }}>{e.start} <b style={{ color: "var(--gold)", marginLeft: 6 }}>{ddayLabel(n)}</b></span>
        </div>
        <div style={{ fontSize: 13.5, color: "var(--text)", lineHeight: 1.75, marginTop: 12, wordBreak: "keep-all" }}>{e.summary}</div>
      </div>

      {e.id === "us-midterm-2026" && <SenateMap e={e} />}

      {e.id === "us-midterm-2026" && <LiveCard L={live} />}

      {e.indicators?.length > 0 && (
        <div style={card}>
          <Title icon="chart-column">현재 상태 지표</Title>
          <div style={{ overflowX: "auto", border: "1px solid var(--grid-line)", borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead><tr><th style={th}>지표</th><th style={th}>값</th><th style={th}>비고</th><th style={th}>기준일</th></tr></thead>
              <tbody>
                {e.indicators.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...td, whiteSpace: "nowrap", color: "var(--text-sub)" }}>{r.label}</td>
                    <td style={{ ...td, fontWeight: 600, color: r.live ? "var(--primary)" : "var(--text)" }}>{r.value}</td>
                    <td style={{ ...td, fontSize: 12, color: "var(--text-sub)" }}>{r.note}<Src url={r.url} /></td>
                    <td style={{ ...td, ...mono, fontSize: 12, whiteSpace: "nowrap", color: "var(--text-sub)" }}>{r.asOf}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ScenarioDetail key={e.id} e={e} onOpenStock={onOpenStock} />

      <Races e={e} />
      <StockScenarioTable key={"t" + e.id} e={e} onOpenStock={onOpenStock} />

      {e.precedents?.length > 0 && (
        <div style={card}>
          <Title icon="lightbulb">{e.precedentsTitle || "과거 비슷한 때 시장 반응"}</Title>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>과거 사례이며 같은 반응이 반복된다는 보장은 없습니다.</div>
          {e.precedents.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: "9px 0", borderTop: i ? "1px solid var(--border-light)" : "none", fontSize: 13 }}>
              <span style={{ color: "var(--text-sub)", width: 210, flexShrink: 0 }}>{r.label}</span>
              <span style={{ color: "var(--text)", fontWeight: 600, flex: 1, minWidth: 220 }}>{r.value}<span style={{ display: "block", fontWeight: 400, fontSize: 12, color: "var(--text-muted)", marginTop: 2, lineHeight: 1.6 }}>{r.note}<Src url={r.url} /></span></span>
            </div>
          ))}
        </div>
      )}

      {e.invalidation?.length > 0 && (
        <div style={card}>
          <Title icon="triangle-alert">무효화 조건</Title>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>아래 조건이 나오면 시나리오와 업종 매핑을 다시 봅니다.</div>
          {e.invalidation.map((t, i) => <div key={i} style={{ fontSize: 13, color: "var(--text)", padding: "7px 0", borderTop: i ? "1px solid var(--border-light)" : "none", lineHeight: 1.6 }}>{t}</div>)}
        </div>
      )}

      {e.checkpoints?.length > 0 && (
        <div style={card}>
          <Title icon="flag">체크포인트</Title>
          {e.checkpoints.map((c, i) => {
            const d = dday(c.date);
            return (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "baseline", padding: "8px 0", borderTop: i ? "1px solid var(--border-light)" : "none", fontSize: 13, color: "var(--text)" }}>
                <span style={{ ...mono, color: "var(--text-sub)", width: 96, flexShrink: 0 }}>{c.approx || c.date}</span>
                <span style={{ flex: 1, lineHeight: 1.6 }}>{c.label}</span>
                <span style={{ ...mono, fontSize: 12, color: c.result ? "var(--text-sub)" : d >= 0 ? "var(--gold)" : "var(--text-muted)", whiteSpace: "nowrap" }}>{c.result || (c.approx && /미정/.test(c.approx) ? "날짜 미정" : d >= 0 ? (c.approx ? "약 " : "") + ddayLabel(d) : "결과 미기록")}</span>
              </div>
            );
          })}
        </div>
      )}

      {e.refs?.length > 0 && (
        <div style={card}>
          <Title icon="chart-line">{e.refsTitle || "과거 패턴"}</Title>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>과거 패턴이며 반복이 보장되지 않습니다.</div>
          {e.refs.map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: "8px 0", borderTop: i ? "1px solid var(--border-light)" : "none", fontSize: 13 }}>
              <span style={{ color: "var(--text-sub)", width: 210, flexShrink: 0 }}>{r.label}</span>
              <span style={{ color: "var(--text)", fontWeight: 600, flex: 1, minWidth: 200 }}>{r.value}<span style={{ display: "block", fontWeight: 400, fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{r.note}</span></span>
            </div>
          ))}
        </div>
      )}

      <div style={card}>
        <Title icon="clipboard-list">결과 기록</Title>
        <div style={{ fontSize: 13, color: e.result ? "var(--text)" : "var(--text-muted)", lineHeight: 1.7 }}>
          {e.result || "이벤트가 끝나면 어느 시나리오가 맞았는지, 매핑한 업종이 실제로 움직였는지를 여기에 기록합니다."}
        </div>
      </div>
    </>
  );
}

// ── 3. 정책 노출 매트릭스 ─────────────────────────────────────
function MatrixTab({ data, watchlist }) {
  const mine = useMemo(() => new Set((watchlist || []).filter(w => w && w.code && !w._type).map(w => w.code)), [watchlist]);
  const names = Array.from(new Set(data.exposures.map(x => x.name)));
  const byTag = data.tags.map(t => ({ tag: t, n: data.exposures.filter(x => x.tag === t).length }));
  const total = data.exposures.length || 1;
  const evTitle = (id) => data.events.find(e => e.id === id)?.title || "";
  return (
    <>
      <div style={card}>
        <Title icon="target">정책 노출 집중도</Title>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          {byTag.map(({ tag, n }) => (
            <div key={tag} style={{ border: "1px solid var(--grid-line)", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 12, color: "var(--text-sub)", marginBottom: 6 }}>{tag}</div>
              <div style={{ ...mono, fontSize: 20, fontWeight: 700, color: n ? "var(--text)" : "var(--text-muted)" }}>{n}<span style={{ fontSize: 12, fontWeight: 400, color: "var(--text-muted)", marginLeft: 4 }}>건 · {Math.round(n / total * 100)}%</span></div>
              <div style={{ height: 4, borderRadius: 2, background: "var(--border-light)", marginTop: 8 }}><div style={{ width: `${n / total * 100}%`, height: "100%", borderRadius: 2, background: TYPE_COLOR[tag] }} /></div>
            </div>
          ))}
        </div>
      </div>

      <div style={card}>
        <Title icon="grid-3x3" right={<div style={{ display: "flex", gap: 10, fontSize: 11, color: "var(--text-sub)", flexWrap: "wrap" }}>{Object.entries(DIR).map(([k, c]) => <span key={k}><span style={{ color: c }}>●</span> {k}</span>)}<span>●●● 강도</span></div>}>종목 × 정책 태그</Title>
        <div style={{ overflowX: "auto", border: "1px solid var(--grid-line)", borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead><tr><th style={th}>종목 · 업종</th>{data.tags.map(t => <th key={t} style={{ ...th, textAlign: "center" }}>{t}</th>)}</tr></thead>
            <tbody>
              {names.map(nm => {
                const rows = data.exposures.filter(x => x.name === nm);
                const first = rows[0];
                return (
                  <tr key={nm}>
                    <td style={{ ...td, whiteSpace: "nowrap", fontWeight: 600 }}>
                      {nm}
                      {first.sector && <span style={{ fontSize: 10, color: "var(--text-muted)", marginLeft: 6, fontWeight: 400 }}>업종</span>}
                      {first.code && mine.has(first.code) && <span style={{ marginLeft: 6 }}><Chip color="#eab308">관심종목</Chip></span>}
                    </td>
                    {data.tags.map(t => {
                      const x = rows.find(r => r.tag === t);
                      return (
                        <td key={t} style={{ ...td, textAlign: "center", borderLeft: "1px solid var(--grid-line)" }} title={x ? x.basis : ""}>
                          {x ? <><span style={{ color: DIR[x.dir], fontWeight: 700, fontSize: 12.5, marginRight: 5 }}>{x.dir}</span><Strength n={x.strength} color={DIR[x.dir]} /></> : <span style={{ color: "var(--text-muted)" }}>·</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={card}>
        <Title icon="file-text">노출 근거</Title>
        {data.exposures.map((x, i) => (
          <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderTop: i ? "1px solid var(--border-light)" : "none", flexWrap: "wrap" }}>
            <div style={{ width: 150, flexShrink: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>{x.name}</div>
              <div style={{ marginTop: 4 }}><span style={{ color: DIR[x.dir], fontWeight: 700, fontSize: 12, marginRight: 5 }}>{x.dir}</span><Strength n={x.strength} color={DIR[x.dir]} /></div>
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.65, wordBreak: "keep-all" }}>{x.basis}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                {evTitle(x.eventId)} · 기준일 {x.asOf}
                {x.review && <span style={{ marginLeft: 8 }}><Chip color="#f59e0b">근거 재확인 필요</Chip></span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── 주식 검색(종목 상세)에 붙는 블록: 그 종목의 정책 노출 ──
export function PolicyExposureCard({ code, onOpen }) {
  const { data } = usePolicyData();
  const rows = data && code ? data.exposures.filter(x => x.code === code) : [];
  if (rows.length === 0) return null;
  return (
    <div style={card}>
      <Title icon="landmark" right={onOpen && <button onClick={onOpen} style={{ border: "none", background: "transparent", color: "var(--link)", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>정책·이벤트 <Ico n="arrow-right" size="0.95em" mr="0" tint={false} /></button>}>정책 노출</Title>
      {rows.map((x, i) => (
        <div key={i} style={{ padding: "8px 0", borderTop: i ? "1px solid var(--border-light)" : "none" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <Chip color={TYPE_COLOR[x.tag] || "#94a3b8"}>{x.tag}</Chip>
            <span style={{ color: DIR[x.dir], fontWeight: 700, fontSize: 12.5 }}>{x.dir}</span><Strength n={x.strength} color={DIR[x.dir]} />
            <span style={{ fontSize: 12, color: "var(--text-sub)" }}>{data.events.find(e => e.id === x.eventId)?.title}</span>
          </div>
          <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.65, wordBreak: "keep-all" }}>{x.basis}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>기준일 {x.asOf} · 투자 자문이 아닌 판단 재료입니다</div>
        </div>
      ))}
    </div>
  );
}

// ── 상단 바로가기: 지금·다음 주요 이벤트 (예: 중간선거 D-26, CES 2027) ──
export function PolicyQuickNav({ onPick, activeId }) {
  const { data } = usePolicyData();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  // 메뉴 밖을 누르면 닫기
  useEffect(() => {
    if (!open) return;
    const close = (ev) => { if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  if (!data) return null;
  const today = kstToday();
  // 다가오는 이벤트(날짜순)는 칩으로, 이미 시작된 정책(232조·301조·대미투자 등)은 '진행 중' 메뉴로
  const upcoming = data.events.filter(e => (e.main || e.scenarios?.length) && e.start >= today).sort((a, b) => a.start.localeCompare(b.start)).slice(0, 6);
  const ongoing = scenarioEvents(data).filter(e => e.start < today && e.status !== "종료");
  const isOn = (e) => activeId ? e.id === activeId : !!e.featured;
  const chip = (on) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
    border: "1px solid " + (on ? "var(--primary)" : "var(--border)"), background: on ? "var(--sb-active-bg)" : "transparent", color: on ? "var(--sb-active-text)" : "var(--text-sub)" });
  const pick = (id) => { setOpen(false); if (onPick) onPick(id); };
  const activeOngoing = ongoing.find(isOn);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 14, minWidth: 0 }}>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", scrollbarWidth: "none", minWidth: 0 }}>
        {upcoming.map((e) => {
          const n = dday(e.start);
          return (
            <button key={e.id} onClick={() => pick(e.id)} title={e.title} style={chip(isOn(e))}>
              {e.short || e.title}
              <span style={{ ...mono, fontSize: 11, color: n <= 7 ? C.up : "var(--gold)" }}>{n > 0 ? `D-${n}` : "D-DAY"}</span>
            </button>
          );
        })}
      </div>
      {ongoing.length > 0 && (
        <div ref={boxRef} style={{ position: "relative", flexShrink: 0 }}>
          <button onClick={() => setOpen(!open)} style={chip(!!activeOngoing)} aria-expanded={open}>
            {activeOngoing ? (activeOngoing.short || activeOngoing.title) : "진행 중인 정책"}
            <span style={{ ...mono, fontSize: 11, color: "#10b981" }}>{activeOngoing ? "진행 중" : ongoing.length}</span>
            <span style={{ fontSize: 9, opacity: 0.7 }}>{open ? "▲" : "▼"}</span>
          </button>
          {open && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50, minWidth: 220, padding: 6, borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg-card)", boxShadow: "0 8px 24px rgba(0,0,0,0.25)" }}>
              {ongoing.map(e => (
                <button key={e.id} onClick={() => pick(e.id)} style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 10px", borderRadius: 7, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: isOn(e) ? 700 : 500, textAlign: "left",
                  background: isOn(e) ? "var(--sb-active-bg)" : "transparent", color: isOn(e) ? "var(--sb-active-text)" : "var(--text)" }}>
                  <span>{e.short || e.title}</span>
                  <span style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>{e.start.slice(2).replace(/-/g, ".")}~</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 페이지 ────────────────────────────────────────────────────
const TABS = [["scenario", "시나리오 맵", "git-fork"], ["calendar", "이벤트 캘린더", "calendar-days"], ["matrix", "정책 노출 매트릭스", "grid-3x3"]];

export default function PolicyEvents({ watchlist, onOpenStock, focus, onActiveChange }) {
  const live = useElectionLive(true); // 상단에 마지막 자동 업데이트 시각 표시용
  const { data, failed } = usePolicyData();
  const [tab, setTab] = useState("scenario"); // 이벤트마다 시나리오가 있음 (2026-10-10), 처음엔 중간선거
  const [eventId, setEventId] = useState(null);
  const [calFocus, setCalFocus] = useState(null);
  // 상단 바로가기에서 고른 이벤트: 시나리오가 있으면 시나리오 맵, 없으면 캘린더에서 펼침
  useEffect(() => {
    if (!focus || !data) return;
    const ev = data.events.find(x => x.id === focus.id);
    if (!ev) return;
    if (ev.scenarios?.length) { setEventId(ev.id); setTab("scenario"); }
    else { setCalFocus(ev.id); setTab("calendar"); }
  }, [focus, data]);
  // 지금 보고 있는 이벤트를 상단 바로가기에 알려 같은 칩이 강조되게 함
  const activeId = !data ? null : tab === "calendar" ? (calFocus || null) : (eventId || defaultEvent(scenarioEvents(data))?.id || null);
  useEffect(() => { if (onActiveChange) onActiveChange(activeId); }, [activeId, onActiveChange]);
  if (failed) return <div style={{ ...card, color: "var(--text-sub)", fontSize: 13 }}>정책·이벤트 데이터를 불러오지 못했습니다. 잠시 후 새로고침해 주세요.</div>;
  if (!data) return <div style={{ ...card, color: "var(--text-muted)", fontSize: 13 }}>불러오는 중…</div>;
  return (
    <div style={{ maxWidth: 1180, margin: "0 auto" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        {TABS.map(([k, t, ic]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: "8px 14px", borderRadius: 9, fontSize: 13, cursor: "pointer", fontWeight: tab === k ? 700 : 500, border: "1px solid " + (tab === k ? "var(--primary)" : "var(--border)"), background: tab === k ? "var(--sb-active-bg)" : "var(--bg-card)", color: tab === k ? "var(--sb-active-text)" : "var(--text-sub)" }}>
            <Ico n={ic} tint={false} />{t}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)", ...mono, textAlign: "right" }}>
          내용 수정 {data.updated}
          {live?.timestamp && <> · <span title="여론조사·확률·경합지 숫자와 뉴스는 매일 06:40(한국 시간)에 자동으로 갱신됩니다">중간선거 숫자 자동 업데이트 {kstStamp(live.timestamp)}</span></>}
        </span>
      </div>
      {tab === "calendar" && <CalendarTab data={data} focusId={calFocus} onOpenScenario={(id) => { setEventId(id); setTab("scenario"); }} />}
      {tab === "scenario" && <ScenarioTab data={data} eventId={eventId} setEventId={setEventId} onOpenStock={onOpenStock} />}
      {tab === "matrix" && <MatrixTab data={data} watchlist={watchlist} />}
      <div style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.8, padding: "4px 4px 20px" }}>
        {data.principles.map((p, i) => <div key={i}>· {p}</div>)}
      </div>
    </div>
  );
}
